import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../../auth/apiClient";
import { mergeEvidenceSummaries, normalizeEvidencePolicy, summarizeEvidence } from "../paymentEmail/evidencePolicy";

const PaymentsDataContext = createContext(undefined);
const RECURRENCE_MODE_REQUIRED = "required";
const RECURRENCE_MODE_OPTIONAL = "optional";
const RECURRENCE_MODE_NOT_ALLOWED = "not_allowed";

const formatPeriod = (start, end) => (start && end ? `${start} to ${end}` : "-");

const buildApiError = (resp, payload, fallbackMessage) => {
  const message =
    payload?.message || payload?.error || fallbackMessage || `Request failed (${resp.status})`;
  const error = new Error(message);
  error.code = payload?.error || payload?.code || null;
  error.details = payload?.details || payload?.missing || payload?.requiredRoles || null;
  error.status = resp.status;
  error.payload = payload;
  return error;
};

const normalizeEvidenceItem = item => {
  if (!item || typeof item !== "object") {
    return {
      type: "Evidence",
      required: false,
      received: false,
      verified: false,
      note: null,
    };
  }
  const documentName =
    item.documentName ||
    item.document_name ||
    item.file_name ||
    item.label ||
    null;
  const documentNames = Array.isArray(item.documentNames)
    ? item.documentNames.map(value => String(value)).filter(Boolean)
    : documentName
      ? [String(documentName)]
      : [];
  const documentIds = Array.isArray(item.documentIds)
    ? item.documentIds.map(value => String(value)).filter(Boolean)
    : item.documentId || item.document_id
      ? [String(item.documentId || item.document_id)]
      : [];
  const documentLinks = Array.isArray(item.documentLinks)
    ? item.documentLinks
        .map(entry => {
          if (!entry || typeof entry !== "object") return null;
          const linkId =
            entry.id || entry.linkId || entry.link_id || entry.documentLinkId || null;
          const documentId = entry.documentId || entry.document_id || null;
          const name = entry.name || entry.documentName || entry.document_name || entry.file_name || entry.label || null;
          if (!linkId && !documentId && !name) return null;
          return {
            id: linkId ? String(linkId) : null,
            documentId: documentId ? String(documentId) : null,
            name: name ? String(name) : null,
          };
        })
        .filter(Boolean)
    : null;
  return {
    id: item.id ? String(item.id) : item.linkId ? String(item.linkId) : null,
    documentId: item.documentId ? String(item.documentId) : item.document_id ? String(item.document_id) : null,
    type:
      item.type ||
      item.evidenceType ||
      item.evidence_type ||
      item.documentType ||
      item.document_type ||
      "Evidence",
    required: !!item.required,
    ...normalizeEvidencePolicy(item),
    received: typeof item.satisfied === "boolean" ? item.satisfied : !!item.received,
    verified: !!item.verified,
    note: item.note || item.notes || null,
    receivedAt: item.receivedAt || item.received_at || null,
    verifiedAt: item.verifiedAt || item.verified_at || null,
    verifiedBy: item.verifiedBy || item.verified_by || null,
    documentName: documentName || (documentNames.length ? documentNames[0] : null),
    documentNames: documentNames.length ? documentNames : null,
    documentIds: documentIds.length ? documentIds : null,
    documentLinks: documentLinks && documentLinks.length ? documentLinks : null,
    source: item.source || item.source_type || null,
  };
};

const normalizeEvidenceList = list =>
  Array.isArray(list) ? list.filter(Boolean).map(normalizeEvidenceItem) : [];

const normalizeRecipients = input => {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input.filter(Boolean).map(value => String(value));
  }
  if (typeof input === "string") {
    return input
      .split(/[;,]+/)
      .map(entry => entry.trim())
      .filter(Boolean);
  }
  if (typeof input === "object") {
    const combined = [];
    ["to", "cc", "bcc"].forEach(key => {
      const value = input[key];
      if (Array.isArray(value)) {
        combined.push(...value.filter(Boolean));
      } else if (typeof value === "string") {
        combined.push(...value.split(/[;,]+/).map(entry => entry.trim()).filter(Boolean));
      }
    });
    return combined.map(value => String(value));
  }
  return [];
};

const normalizeCommunication = raw => {
  if (!raw || typeof raw !== "object") return null;
  const recipients = normalizeRecipients(
    raw.recipients || raw.recipients_json || raw.to || raw.cc || raw.bcc
  );
  return {
    id: raw.id ? String(raw.id) : "",
    packetId: raw.packetId || raw.payment_packet_id || raw.paymentPacketId || null,
    lineId: raw.lineId || raw.payment_packet_line_id || null,
    direction: raw.direction || "outbound",
    channel: raw.channel || "email",
    sender: raw.sender || raw.sender_label || raw.sender_name || raw.sender_email || null,
    recipients,
    subject: raw.subject || null,
    body: raw.body || raw.message || null,
    template: raw.template || raw.template_key || "custom",
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    sentOn: raw.sentOn || raw.sent_on || raw.sent_at || raw.created_at || new Date().toISOString(),
  };
};

const normalizeInterventionCodeValue = value => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return String(Math.trunc(numeric));
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/[^\d]/g, "");
  if (!digits) return null;
  const parsed = Number.parseInt(digits, 10);
  return Number.isFinite(parsed) ? String(parsed) : null;
};

const normalizePaymentTypeCode = value => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

const normalizePayeeTypeCode = value => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

const normalizeRecurrenceMode = value => {
  if (typeof value !== "string") return RECURRENCE_MODE_NOT_ALLOWED;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === RECURRENCE_MODE_REQUIRED) return RECURRENCE_MODE_REQUIRED;
  if (normalized === RECURRENCE_MODE_OPTIONAL) return RECURRENCE_MODE_OPTIONAL;
  if (normalized === RECURRENCE_MODE_NOT_ALLOWED || normalized === "disabled") {
    return RECURRENCE_MODE_NOT_ALLOWED;
  }
  return RECURRENCE_MODE_NOT_ALLOWED;
};

const normalizePacketStatusValue = value => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  return ["draft", "ready_to_send", "submitted", "confirmed", "cancelled"].includes(normalized)
    ? normalized
    : null;
};

const normalizeLineStatusValue = value => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  return ["needs_evidence", "ready_to_send", "submitted", "paid", "held", "cancelled"].includes(normalized)
    ? normalized
    : null;
};

const normalizeFollowUpStatusValue = value => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return null;
  return [
    "not_required",
    "sent_to_finance",
    "follow_up_needed",
    "follow_up_logged",
    "reported_paid",
    "confirmed_by_evidence",
    "stale_no_response",
    "cancelled_not_proceeding",
  ].includes(normalized)
    ? normalized
    : null;
};

const normalizeFollowUpEvent = raw => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ? String(raw.id) : "",
    packetId: raw.packetId || raw.payment_packet_id || null,
    lineId: raw.lineId || raw.payment_packet_line_id || null,
    fromStatus: normalizeFollowUpStatusValue(raw.fromStatus || raw.from_status),
    toStatus: normalizeFollowUpStatusValue(raw.toStatus || raw.to_status || raw.status),
    label: raw.label || null,
    actor: raw.actor || raw.actor_name || raw.actor_email || null,
    note: raw.note || raw.notes || null,
    dueAt: raw.dueAt || raw.due_at || null,
    documentId: raw.documentId || raw.document_id || null,
    createdAt: raw.createdAt || raw.created_at || null,
  };
};

const normalizeRecurrencePolicies = raw => {
  const map = new Map();
  const add = (codeRaw, modeRaw) => {
    const code = normalizePaymentTypeCode(codeRaw);
    if (!code) return;
    map.set(code, normalizeRecurrenceMode(modeRaw));
  };
  if (Array.isArray(raw)) {
    raw.forEach(entry => {
      if (!entry || typeof entry !== "object") return;
      add(
        entry.code ?? entry.type ?? entry.paymentType ?? entry.payment_type ?? null,
        entry.mode ?? entry.recurrenceMode ?? entry.recurrence_mode ?? entry.recurrence?.mode ?? null,
      );
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      if (value && typeof value === "object") {
        add(key, value.mode ?? value.recurrenceMode ?? value.recurrence_mode ?? value.recurrence?.mode);
        return;
      }
      add(key, value);
    });
  }
  return Array.from(map.entries())
    .map(([code, mode]) => ({ code, mode }))
    .sort((a, b) => String(a.code).localeCompare(String(b.code)));
};

const buildLine = line => {
  const amount = Number(line.amount || 0);
  const evidenceSummary = summarizeEvidence(line.evidenceChecklist ?? []);
  return {
    ...line,
    amount: Number.isFinite(amount) ? amount : 0,
    servicePeriodLabel: formatPeriod(line.servicePeriodStart, line.servicePeriodEnd),
    evidenceSummary,
  };
};

const normalizeLine = raw => {
  if (!raw || typeof raw !== "object") {
    return buildLine({ amount: 0, evidenceChecklist: [] });
  }
  return buildLine({
    ...raw,
    id: raw.id ? String(raw.id) : "",
    packetId: raw.packetId || raw.payment_packet_id || null,
    paymentType: raw.paymentType || raw.payment_type || null,
    payeeType: raw.payeeType || raw.payee_type || null,
    payeeName: raw.payeeName || raw.payee_name || null,
    payeeReference: raw.payeeReference || raw.payee_reference || null,
    potId: raw.potId || raw.budget_pot_id || null,
    potName: raw.potName || raw.pot_name || null,
    fundingStream: raw.fundingStream || raw.funding_stream || null,
    reportingUnit: raw.reportingUnit || raw.reporting_unit || null,
    holdReason: raw.holdReason || raw.hold_reason || null,
    servicePeriodStart: raw.servicePeriodStart || raw.service_period_start || null,
    servicePeriodEnd: raw.servicePeriodEnd || raw.service_period_end || null,
    requestedPaymentDate: raw.requestedPaymentDate || raw.requested_payment_date || null,
    invoiceReferenceNumber: raw.invoiceReferenceNumber || raw.invoice_reference_number || null,
    status: normalizeLineStatusValue(raw.status) || "needs_evidence",
    followUpStatus: normalizeFollowUpStatusValue(raw.followUpStatus || raw.follow_up_status) || "not_required",
    followUpLabel: raw.followUpLabel || raw.follow_up_label || null,
    followUpDueAt: raw.followUpDueAt || raw.follow_up_due_at || null,
    followUpUpdatedAt: raw.followUpUpdatedAt || raw.follow_up_updated_at || null,
    followUpEvents: Array.isArray(raw.followUpEvents || raw.follow_up_events)
      ? (raw.followUpEvents || raw.follow_up_events).map(normalizeFollowUpEvent).filter(Boolean)
      : [],
    paidAt: raw.paidAt || raw.paid_at || null,
    paymentReference: raw.paymentReference || raw.payment_reference || null,
    paymentProofDocumentId: raw.paymentProofDocumentId || raw.payment_proof_document_id || null,
    authorization: raw.authorization || null,
    batch: raw.batch || raw.batchInfo || raw.batch_info || null,
    evidenceChecklist: normalizeEvidenceList(raw.evidenceChecklist || raw.evidence_checklist),
  });
};

const buildPacket = packet => {
  const lines = (packet.lines ?? []).map(line => (line.evidenceSummary ? line : buildLine(line)));
  const baselineSummary = summarizeEvidence(packet.baselineEvidence ?? []);
  const lineEvidenceSummary = mergeEvidenceSummaries(lines.map(line => line.evidenceSummary));
  const evidenceSummary = mergeEvidenceSummaries([baselineSummary, lineEvidenceSummary]);
  const paymentTypes = Array.from(new Set(lines.map(line => line.paymentType).filter(Boolean)));
  const totalAmount = lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const streamTotals = { CRF: 0, EI: 0 };
  lines.forEach(line => {
    const stream = line.fundingStream || "Unclassified";
    streamTotals[stream] = (streamTotals[stream] ?? 0) + (Number(line.amount) || 0);
  });
  const ageDays = packet.submittedOn
    ? Math.max(0, Math.floor((Date.now() - new Date(packet.submittedOn).getTime()) / 86400000))
    : 0;

  return {
    ...packet,
    lines,
    baselineEvidenceSummary: baselineSummary,
    evidenceSummary,
    paymentTypes,
    totalAmount,
    streamTotals,
    ageDays,
  };
};

const normalizePacket = packet => {
  const riskFlagsInput = Array.isArray(packet?.riskFlags)
    ? packet.riskFlags
    : Array.isArray(packet?.risk_flags)
      ? packet.risk_flags
      : [];
  return buildPacket({
    ...packet,
    id: packet?.id ? String(packet.id) : "",
    caseId: packet?.caseId || packet?.case_id || null,
    caseNumber: packet?.caseNumber || packet?.case_number || null,
    applicationId:
      packet?.applicationId || packet?.application_id
        ? String(packet.applicationId || packet.application_id)
        : null,
    applicantUserId:
      packet?.applicantUserId || packet?.applicant_user_id
        ? String(packet.applicantUserId || packet.applicant_user_id)
        : null,
    clientId: packet?.clientId || packet?.client_id || null,
    clientName: packet?.clientName || packet?.client_name || null,
    interventionId: packet?.interventionId || packet?.intervention_id || null,
    interventionName: packet?.interventionName || packet?.intervention_name || null,
    interventionCode: packet?.interventionCode || packet?.intervention_code || null,
    reportingUnit: packet?.reportingUnit || packet?.reporting_unit || null,
    potId: packet?.potId || packet?.pot_id || null,
    potName: packet?.potName || packet?.pot_name || null,
    requester: packet?.requester || packet?.requester_name || null,
    requesterRole: packet?.requesterRole || packet?.requester_role || null,
    status: normalizePacketStatusValue(packet?.status) || "draft",
    followUpStatus: normalizeFollowUpStatusValue(packet?.followUpStatus || packet?.follow_up_status) || "not_required",
    followUpLabel: packet?.followUpLabel || packet?.follow_up_label || null,
    followUpDueAt: packet?.followUpDueAt || packet?.follow_up_due_at || null,
    followUpUpdatedAt: packet?.followUpUpdatedAt || packet?.follow_up_updated_at || null,
    submittedOn: packet?.submittedOn || packet?.submitted_on || null,
    dueBy: packet?.dueBy || packet?.due_by || null,
    notes: packet?.notes || packet?.notes_internal || null,
    riskFlags: riskFlagsInput.filter(Boolean),
    baselineEvidence: normalizeEvidenceList(packet?.baselineEvidence || packet?.baseline_evidence),
    lines: Array.isArray(packet?.lines) ? packet.lines.filter(Boolean).map(normalizeLine) : [],
    approvals: Array.isArray(packet?.approvals) ? packet.approvals : [],
    timeline: Array.isArray(packet?.timeline) ? packet.timeline : [],
    followUpEvents: Array.isArray(packet?.followUpEvents || packet?.follow_up_events)
      ? (packet.followUpEvents || packet.follow_up_events).map(normalizeFollowUpEvent).filter(Boolean)
      : [],
    documents: Array.isArray(packet?.documents) ? packet.documents : [],
    duplicateWarnings: Array.isArray(packet?.duplicateWarnings) ? packet.duplicateWarnings : [],
    overrideHistory: Array.isArray(packet?.overrideHistory) ? packet.overrideHistory : [],
    turnaroundDays: Number.isFinite(packet?.turnaroundDays) ? packet.turnaroundDays : packet?.turnaround_days || null,
  });
};

const normalizePaymentTypeMappingPayload = payload => {
  if (!payload || typeof payload !== "object") return null;
  const paymentTypesRaw = Array.isArray(payload.paymentTypes)
    ? payload.paymentTypes
    : Array.isArray(payload.payment_types)
      ? payload.payment_types
      : [];
  const paymentTypes = paymentTypesRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizePaymentTypeCode(
        entry.code ?? entry.value ?? entry.paymentType ?? entry.payment_type ?? null,
      );
      if (!code) return null;
      const labelRaw = entry.label ?? entry.name ?? null;
      const label = typeof labelRaw === "string" && labelRaw.trim() ? labelRaw.trim() : code;
      return { code, label };
    })
    .filter(Boolean);
  const interventionsRaw = Array.isArray(payload.interventions) ? payload.interventions : [];
  const payeeTypesRaw = Array.isArray(payload.payeeTypes)
    ? payload.payeeTypes
    : Array.isArray(payload.payee_types)
      ? payload.payee_types
      : [];
  const payeeTypes = payeeTypesRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizePayeeTypeCode(
        entry.code ?? entry.value ?? entry.payeeType ?? entry.payee_type ?? null,
      );
      if (!code) return null;
      const labelRaw = entry.label ?? entry.name ?? null;
      const descriptionRaw = entry.description ?? entry.helpText ?? entry.help_text ?? null;
      return {
        code,
        label: typeof labelRaw === "string" && labelRaw.trim() ? labelRaw.trim() : code,
        description:
          typeof descriptionRaw === "string" && descriptionRaw.trim()
            ? descriptionRaw.trim()
            : null,
      };
    })
    .filter(Boolean);
  const interventions = interventionsRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const codeRaw = entry.code ?? entry.interventionCode ?? entry.intervention_code ?? null;
      const code = normalizeInterventionCodeValue(codeRaw);
      if (!code) return null;
      const typesRaw =
        entry.availablePaymentTypes ||
        entry.available_payment_types ||
        entry.paymentTypes ||
        entry.payment_types ||
        [];
      const types = Array.isArray(typesRaw)
        ? Array.from(
            new Set(
              typesRaw
                .map(value => (value === null || value === undefined ? "" : String(value).trim()))
                .filter(Boolean),
            ),
          )
        : [];
      return {
        code,
        name: entry.name || entry.label || null,
        availablePaymentTypes: types,
      };
    })
    .filter(Boolean);
  const recurrencePolicies = normalizeRecurrencePolicies(
    payload.recurrencePolicies || payload.recurrence_policies || payload.paymentTypeRecurrence,
  );
  return {
    ...payload,
    enabled: payload.enabled !== false,
    paymentTypes,
    payeeTypes,
    interventions,
    recurrencePolicies,
  };
};

const buildPaymentTypeMappingLookup = mapping => {
  const lookup = new Map();
  if (!mapping?.interventions) return lookup;
  mapping.interventions.forEach(entry => {
    const code = normalizeInterventionCodeValue(entry?.code);
    if (!code) return;
    const types = Array.isArray(entry.availablePaymentTypes)
      ? entry.availablePaymentTypes.filter(Boolean)
      : [];
    lookup.set(code, new Set(types));
  });
  return lookup;
};

const buildRecurrencePolicyLookup = mapping => {
  const lookup = new Map();
  const list = Array.isArray(mapping?.recurrencePolicies) ? mapping.recurrencePolicies : [];
  list.forEach(entry => {
    const code = normalizePaymentTypeCode(entry?.code);
    if (!code) return;
    lookup.set(code, normalizeRecurrenceMode(entry?.mode));
  });
  return lookup;
};

const computeSlaSnapshot = requests => {
  const snapshot = {
    draftsNeedingEvidence: 0,
    submitted: 0,
    overdueEvidence: 0,
    avgSubmissionAgeDays: 0,
  };
  const submissionAges = [];

  requests.forEach(packet => {
    const statusValue = normalizePacketStatusValue(packet.status) || "draft";
    const statusKey =
      statusValue === "draft" || statusValue === "ready_to_send"
        ? "draft"
        : statusValue === "cancelled"
          ? "cancelled"
          : "submitted";
    if (statusKey === "draft") {
      const missing = packet.evidenceSummary?.missing ?? 0;
      if (missing > 0) {
        snapshot.draftsNeedingEvidence += 1;
      }
    } else if (statusKey === "submitted") {
      snapshot.submitted += 1;
      if (Number.isFinite(packet.ageDays)) {
        submissionAges.push(packet.ageDays);
      }
    }
    if ((packet.riskFlags ?? []).some(flag => flag.toLowerCase().includes("overdue"))) {
      snapshot.overdueEvidence += 1;
    }
  });

  if (submissionAges.length) {
    const total = submissionAges.reduce((sum, value) => sum + value, 0);
    snapshot.avgSubmissionAgeDays = total / submissionAges.length;
  }

  return snapshot;
};

const normalizeFilterValue = value => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const buildPaymentsQuery = ({
  status,
  statuses,
  caseId,
  clientId,
  interventionId,
  reportingUnit,
  limit,
} = {}) => {
  const params = new URLSearchParams();
  const normalizedStatus = normalizeFilterValue(status);
  const normalizedCaseId = normalizeFilterValue(caseId);
  const normalizedClientId = normalizeFilterValue(clientId);
  const normalizedInterventionId = normalizeFilterValue(interventionId);
  const normalizedReportingUnit = normalizeFilterValue(reportingUnit);
  const normalizedLimit = normalizeFilterValue(limit);
  const statusList = Array.isArray(statuses)
    ? statuses.map(item => normalizeFilterValue(item)).filter(Boolean)
    : normalizeFilterValue(statuses)
      ? String(statuses)
          .split(",")
          .map(item => normalizeFilterValue(item))
          .filter(Boolean)
      : [];

  if (normalizedStatus) {
    params.set("status", normalizedStatus);
  }
  if (statusList.length) {
    params.set("statuses", statusList.join(","));
  }
  if (normalizedCaseId) {
    params.set("caseId", normalizedCaseId);
  }
  if (normalizedClientId) {
    params.set("clientId", normalizedClientId);
  }
  if (normalizedInterventionId) {
    params.set("interventionId", normalizedInterventionId);
  }
  if (normalizedReportingUnit) {
    params.set("reportingUnit", normalizedReportingUnit);
  }
  if (normalizedLimit) {
    params.set("limit", normalizedLimit);
  }

  return params.toString();
};

export const PaymentsDataProvider = ({ children, filters = {}, autoSelectFirst = true, initialRequestId = null }) => {
  const {
    status,
    statuses,
    caseId,
    clientId,
    interventionId,
    reportingUnit,
    limit,
  } = filters || {};
  const queryString = useMemo(
    () =>
      buildPaymentsQuery({
        status,
        statuses,
        caseId,
        clientId,
        interventionId,
        reportingUnit,
        limit,
      }),
    [status, statuses, caseId, clientId, interventionId, reportingUnit, limit]
  );
  const scopeKey = queryString || "all";
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const [paymentSubmissionMode, setPaymentSubmissionMode] = useState("email");
  useEffect(() => {
    const controller = new AbortController();
    const loadDeliverySettings = async () => {
      try {
        const response = await apiFetch("/api/finance/payment-delivery-settings", { signal: controller.signal });
        if (!response.ok) return;
        const settings = await response.json();
        if (!controller.signal.aborted) setPaymentSubmissionMode(settings.submissionMode === "intacct_rest" ? "intacct_rest" : "email");
      } catch (_) {
        // Labels fall back to email; the server still determines the actual handoff.
      }
    };
    loadDeliverySettings();
    return () => controller.abort();
  }, []);
  const [requestState, setRequestState] = useState({ scopeKey: null, items: [] });
  const requests = requestState.scopeKey === scopeKey ? requestState.items : [];
  const setRequests = useCallback(nextValue => {
    setRequestState(previousState => {
      const previousItems = previousState.scopeKey === scopeKey ? previousState.items : [];
      const items = typeof nextValue === "function" ? nextValue(previousItems) : nextValue;
      return { scopeKey, items: Array.isArray(items) ? items : [] };
    });
  }, [scopeKey]);
  const [selectedRequestId, setSelectedRequestId] = useState(initialRequestId ? String(initialRequestId) : null);
  const [communicationState, setCommunicationState] = useState({ scopeKey: null, items: [] });
  const activeSelectedRequestId = selectedRequestId && requests.some(entry => entry.id === selectedRequestId)
    ? selectedRequestId
    : null;
  const communicationOwnerKey = `${scopeKey}|${activeSelectedRequestId || "none"}`;
  const communications = communicationState.scopeKey === communicationOwnerKey
    ? communicationState.items
    : [];
  const setCommunications = useCallback(nextValue => {
    setCommunicationState(previousState => {
      const previousItems = previousState.scopeKey === communicationOwnerKey
        ? previousState.items
        : [];
      const items = typeof nextValue === "function" ? nextValue(previousItems) : nextValue;
      return { scopeKey: communicationOwnerKey, items: Array.isArray(items) ? items : [] };
    });
  }, [communicationOwnerKey]);
  const requestGenerationRef = useRef(0);
  const communicationGenerationRef = useRef(0);
  const requestAbortRef = useRef(null);
  const communicationAbortRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentTypeMapping, setPaymentTypeMapping] = useState(null);
  const [paymentTypeMappingLoading, setPaymentTypeMappingLoading] = useState(false);

  const loadCommunications = useCallback(async (packetIdOverride = null) => {
    const scopedPacketId =
      packetIdOverride !== null && packetIdOverride !== undefined && packetIdOverride !== ""
        ? String(packetIdOverride)
        : "";
    const ownerKey = `${scopeKey}|${scopedPacketId || "none"}`;
    const generation = communicationGenerationRef.current + 1;
    communicationGenerationRef.current = generation;
    communicationAbortRef.current?.abort();
    const controller = new AbortController();
    communicationAbortRef.current = controller;
    if (!scopedPacketId) {
      setCommunicationState({ scopeKey: ownerKey, items: [] });
      return;
    }
    try {
      const endpoint = scopedPacketId
        ? `/api/finance/payment-communications?packetId=${encodeURIComponent(scopedPacketId)}`
        : "/api/finance/payment-communications";
      const resp = await apiFetch(endpoint, { signal: controller.signal });
      if (generation !== communicationGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      if (!resp.ok) {
        if (!scopedPacketId && (resp.status === 400 || resp.status === 403)) {
          setCommunicationState({ scopeKey: ownerKey, items: [] });
          return;
        }
        throw new Error(`Communications load failed (${resp.status})`);
      }
      const data = await resp.json();
      const normalized = Array.isArray(data)
        ? data.map(normalizeCommunication).filter(Boolean)
        : [];
      if (generation !== communicationGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      setCommunicationState({ scopeKey: ownerKey, items: normalized });
    } catch (err) {
      if (controller.signal.aborted || generation !== communicationGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      console.error("[Payments] failed to load communications", err);
      setCommunicationState({ scopeKey: ownerKey, items: [] });
    }
  }, [scopeKey]);

  const loadPaymentTypeMapping = useCallback(async () => {
    setPaymentTypeMappingLoading(true);
    try {
      const resp = await apiFetch("/api/finance/payment-intervention-type-map");
      if (!resp.ok) {
        throw new Error(`Mapping load failed (${resp.status})`);
      }
      const payload = await resp.json();
      const normalized = normalizePaymentTypeMappingPayload(payload);
      setPaymentTypeMapping(normalized);
    } catch (err) {
      console.error("[Payments] failed to load payment type mapping", err);
      setPaymentTypeMapping(null);
    } finally {
      setPaymentTypeMappingLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    requestAbortRef.current?.abort();
    const controller = new AbortController();
    requestAbortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const endpoint = queryString
        ? `/api/finance/payment-packets?${queryString}`
        : "/api/finance/payment-packets";
      const resp = await apiFetch(endpoint, { signal: controller.signal });
      if (generation !== requestGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      if (!resp.ok) {
        throw new Error(`Load failed (${resp.status})`);
      }
      const data = await resp.json();
      const normalized = Array.isArray(data) ? data.map(normalizePacket) : [];
      if (generation !== requestGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      setRequests(normalized);
    } catch (err) {
      if (controller.signal.aborted || generation !== requestGenerationRef.current || scopeKeyRef.current !== scopeKey) return;
      console.error("[Payments] failed to load packets", err);
      setError(err.message || "Failed to load payment packets");
      setRequests([]);
    } finally {
      if (generation === requestGenerationRef.current && scopeKeyRef.current === scopeKey) {
        setLoading(false);
      }
    }
  }, [queryString, scopeKey, setRequests]);

  useEffect(() => () => {
    requestAbortRef.current?.abort();
    communicationAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    loadCommunications(activeSelectedRequestId);
  }, [activeSelectedRequestId, loadCommunications, scopeKey]);

  useEffect(() => {
    loadPaymentTypeMapping();
  }, [loadPaymentTypeMapping]);

  useEffect(() => {
    if (!requests.length) {
      return;
    }
    if (selectedRequestId && requests.some(entry => entry.id === selectedRequestId)) {
      return;
    }
    if (autoSelectFirst) {
      setSelectedRequestId(requests[0].id);
      return;
    }
    setSelectedRequestId(null);
  }, [autoSelectFirst, requests, selectedRequestId]);

  const paymentScopeError = useCallback(resource => {
    const error = new Error(`Payment ${resource} is outside the active workspace scope.`);
    error.code = "PAYMENT_SCOPE_NOT_READY";
    return error;
  }, []);

  const assertPacketInCurrentScope = useCallback(packetId => {
    const id = packetId === null || packetId === undefined ? "" : String(packetId);
    const packet = requests.find(entry => String(entry.id) === id) || null;
    if (!packet) throw paymentScopeError("packet");
    return packet;
  }, [paymentScopeError, requests]);

  const assertLineInCurrentScope = useCallback(lineId => {
    const id = lineId === null || lineId === undefined ? "" : String(lineId);
    const packet = requests.find(entry =>
      (entry.lines || []).some(line => String(line.id) === id)
    ) || null;
    if (!packet) throw paymentScopeError("line");
    return packet;
  }, [paymentScopeError, requests]);

  const assertDocumentLinkInCurrentScope = useCallback(documentLinkId => {
    const id = documentLinkId === null || documentLinkId === undefined ? "" : String(documentLinkId);
    const matches = item => String(item?.id || item?.linkId || item?.documentLinkId || "") === id;
    const evidenceMatches = list => (Array.isArray(list) ? list : []).some(item =>
      matches(item) || (item?.documentLinks || []).some(matches)
    );
    const packet = requests.find(entry =>
      (entry.documents || []).some(matches) ||
      evidenceMatches(entry.baselineEvidence) ||
      (entry.lines || []).some(line => evidenceMatches(line.evidenceChecklist))
    ) || null;
    if (!packet) throw paymentScopeError("document link");
    return packet;
  }, [paymentScopeError, requests]);

  const selectRequest = useCallback(requestId => {
    if (requestId === null || requestId === undefined) {
      setSelectedRequestId(null);
      return;
    }
    assertPacketInCurrentScope(requestId);
    setSelectedRequestId(String(requestId));
  }, [assertPacketInCurrentScope]);

  const updatePacketStatus = useCallback(async (packetId, status, options = {}) => {
    if (!packetId || !status) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status,
            notes: options.notes || null,
            override: options.override === true,
            overrideReason: options.overrideReason || null,
            overrideType: options.overrideType || null,
          }),
        }
      );
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw buildApiError(resp, payload, `Update failed (${resp.status})`);
      }
      const updated = normalizePacket(await resp.json());
      setRequests(prev => {
        const next = Array.isArray(prev) ? prev.slice() : [];
        const index = next.findIndex(entry => entry.id === updated.id);
        if (index >= 0) {
          next[index] = updated;
          return next;
        }
        return [updated, ...next];
      });
      setSelectedRequestId(updated.id);
      loadCommunications(updated.id);
      return updated;
    } catch (err) {
      console.error("[Payments] failed to update packet status", err);
      const message = err.message || "Failed to update packet status";
      setError(message);
      throw err;
    }
  }, [assertPacketInCurrentScope, loadCommunications]);

  const validatePacket = useCallback(async packetId => {
    if (!packetId) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, payload, `Validation failed (${resp.status})`);
      }
      const updated = normalizePacket(payload);
      setRequests(prev => {
        const next = Array.isArray(prev) ? prev.slice() : [];
        const index = next.findIndex(entry => entry.id === updated.id);
        if (index >= 0) {
          next[index] = updated;
          return next;
        }
        return [updated, ...next];
      });
      setSelectedRequestId(updated.id);
      return updated;
    } catch (err) {
      console.error("[Payments] failed to validate packet", err);
      const message = err.message || "Failed to validate payment packet";
      setError(message);
      throw err;
    }
  }, [assertPacketInCurrentScope]);

  const updateLineStatus = useCallback(async (lineId, status, options = {}) => {
    if (!lineId || !status) return null;
    assertLineInCurrentScope(lineId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-lines/${encodeURIComponent(lineId)}/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status, ...options }),
        }
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, payload, `Update failed (${resp.status})`);
      }
      const updatedLine = normalizeLine(payload);
      let updatedPacket = null;
      if (updatedLine?.packetId) {
        const packetResp = await apiFetch(
          `/api/finance/payment-packets/${encodeURIComponent(updatedLine.packetId)}`
        );
        if (packetResp.ok) {
          updatedPacket = normalizePacket(await packetResp.json());
        }
      }
      if (updatedPacket) {
        setRequests(prev => {
          const next = Array.isArray(prev) ? prev.slice() : [];
          const index = next.findIndex(entry => entry.id === updatedPacket.id);
          if (index >= 0) {
            next[index] = updatedPacket;
            return next;
          }
          return [updatedPacket, ...next];
        });
        setSelectedRequestId(updatedPacket.id);
      } else if (updatedLine?.packetId) {
        setRequests(prev =>
          prev.map(packet => {
            if (packet.id !== updatedLine.packetId) return packet;
            const nextLines = (packet.lines || []).map(line =>
              line.id === updatedLine.id ? updatedLine : line
            );
            return normalizePacket({ ...packet, lines: nextLines });
          })
        );
      }
      return updatedLine;
    } catch (err) {
      console.error("[Payments] failed to update line status", err);
      const message = err.message || "Failed to update payment line status";
      setError(message);
      throw err;
    }
  }, [assertLineInCurrentScope]);

  const updateLine = useCallback(async (lineId, payload = {}) => {
    if (!lineId) return null;
    assertLineInCurrentScope(lineId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-lines/${encodeURIComponent(lineId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, data, `Update failed (${resp.status})`);
      }
      const updatedLine = normalizeLine(data);
      let updatedPacket = null;
      if (updatedLine?.packetId) {
        const packetResp = await apiFetch(
          `/api/finance/payment-packets/${encodeURIComponent(updatedLine.packetId)}`
        );
        if (packetResp.ok) {
          updatedPacket = normalizePacket(await packetResp.json());
        }
      }
      if (updatedPacket) {
        setRequests(prev => {
          const next = Array.isArray(prev) ? prev.slice() : [];
          const index = next.findIndex(entry => entry.id === updatedPacket.id);
          if (index >= 0) {
            next[index] = updatedPacket;
            return next;
          }
          return [updatedPacket, ...next];
        });
        setSelectedRequestId(updatedPacket.id);
      } else if (updatedLine?.packetId) {
        setRequests(prev =>
          prev.map(packet => {
            if (packet.id !== updatedLine.packetId) return packet;
            const nextLines = (packet.lines || []).map(line =>
              line.id === updatedLine.id ? updatedLine : line
            );
            return normalizePacket({ ...packet, lines: nextLines });
          })
        );
      }
      return updatedLine;
    } catch (err) {
      console.error("[Payments] failed to update payment line", err);
      const message = err.message || "Failed to update payment line";
      setError(message);
      throw err;
    }
  }, [assertLineInCurrentScope]);

  const logPaymentFollowUp = useCallback(async (packetId, payload = {}) => {
    if (!packetId) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}/follow-up`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, data, `Follow-up update failed (${resp.status})`);
      }
      const updated = normalizePacket(data?.packet || data);
      if (updated?.id) {
        setRequests(prev => {
          const next = Array.isArray(prev) ? prev.slice() : [];
          const index = next.findIndex(entry => entry.id === updated.id);
          if (index >= 0) {
            next[index] = updated;
            return next;
          }
          return [updated, ...next];
        });
        setSelectedRequestId(updated.id);
      }
      return {
        packet: updated,
        event: normalizeFollowUpEvent(data?.event),
      };
    } catch (err) {
      console.error("[Payments] failed to log follow-up", err);
      const message = err.message || "Failed to log payment follow-up";
      setError(message);
      throw err;
    }
  }, [assertPacketInCurrentScope]);

  const deleteLine = useCallback(async lineId => {
    if (!lineId) return null;
    assertLineInCurrentScope(lineId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-lines/${encodeURIComponent(lineId)}`,
        { method: "DELETE" }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, data, `Delete failed (${resp.status})`);
      }
      if (data?.id) {
        const updated = normalizePacket(data);
        setRequests(prev => {
          const next = Array.isArray(prev) ? prev.slice() : [];
          const index = next.findIndex(entry => entry.id === updated.id);
          if (index >= 0) {
            next[index] = updated;
            return next;
          }
          return [updated, ...next];
        });
        setSelectedRequestId(updated.id);
        return updated;
      }
      await loadRequests();
      return data;
    } catch (err) {
      console.error("[Payments] failed to delete payment line", err);
      const message = err.message || "Failed to delete payment line";
      setError(message);
      throw err;
    }
  }, [assertLineInCurrentScope, loadRequests]);

  const createPacket = useCallback(async payload => {
    try {
      const resp = await apiFetch("/api/finance/payment-packets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, data, `Create failed (${resp.status})`);
      }
      const created = normalizePacket(data);
      setRequests(prev => {
        const next = Array.isArray(prev) ? prev.slice() : [];
        const existingIndex = next.findIndex(entry => entry.id === created.id);
        if (existingIndex >= 0) {
          next[existingIndex] = created;
          return next;
        }
        return [created, ...next];
      });
      setSelectedRequestId(created.id);
      return created;
    } catch (err) {
      console.error("[Payments] failed to create payment packet", err);
      const message = err.message || "Failed to create payment packet";
      setError(message);
      const wrappedError = new Error(message);
      wrappedError.code = err?.code || null;
      wrappedError.details = err?.details || err?.payload?.details || null;
      wrappedError.status = err?.status || null;
      wrappedError.payload = err?.payload || null;
      throw wrappedError;
    }
  }, []);

  const deletePacket = useCallback(async packetId => {
    if (!packetId) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}`,
        { method: "DELETE" }
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, payload, `Delete failed (${resp.status})`);
      }
      setRequests(prev => {
        const next = Array.isArray(prev)
          ? prev.filter(entry => entry.id !== String(packetId))
          : [];
        if (selectedRequestId && String(selectedRequestId) === String(packetId)) {
          setSelectedRequestId(next[0]?.id ?? null);
        }
        return next;
      });
      return payload;
    } catch (err) {
      console.error("[Payments] failed to delete payment packet", err);
      const message = err.message || "Failed to delete payment packet";
      setError(message);
      throw err;
    }
  }, [assertPacketInCurrentScope, selectedRequestId]);

  const addPacketLines = useCallback(async (packetId, payload = {}) => {
    if (!packetId) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}/lines`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw buildApiError(resp, data, `Create failed (${resp.status})`);
      }
      const updated = normalizePacket(data);
      setRequests(prev => {
        const next = Array.isArray(prev) ? prev.slice() : [];
        const index = next.findIndex(entry => entry.id === updated.id);
        if (index >= 0) {
          next[index] = updated;
          return next;
        }
        return [updated, ...next];
      });
      setSelectedRequestId(updated.id);
      return updated;
    } catch (err) {
      console.error("[Payments] failed to add payment lines", err);
      const message = err.message || "Failed to add payment lines";
      setError(message);
      throw err;
    }
  }, [assertPacketInCurrentScope]);

  const createBatch = useCallback(async lineIds => {
    const normalized = Array.isArray(lineIds)
      ? lineIds.map(id => String(id)).filter(Boolean)
      : [];
    if (!normalized.length) return null;
    normalized.forEach(assertLineInCurrentScope);
    try {
      const resp = await apiFetch("/api/finance/payment-batches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lineIds: normalized }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.message || payload?.error || `Create batch failed (${resp.status})`);
      }
      await loadRequests();
      return payload;
    } catch (err) {
      console.error("[Payments] failed to create batch", err);
      const message = err.message || "Failed to create payment batch";
      setError(message);
      throw new Error(message);
    }
  }, [assertLineInCurrentScope, loadRequests]);

  const updateBatchStatus = useCallback(async (batchId, status) => {
    if (!batchId || !status) return null;
    try {
      const resp = await apiFetch(
        `/api/finance/payment-batches/${encodeURIComponent(batchId)}/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.message || payload?.error || `Update failed (${resp.status})`);
      }
      await loadRequests();
      return payload;
    } catch (err) {
      console.error("[Payments] failed to update batch status", err);
      const message = err.message || "Failed to update payment batch status";
      setError(message);
      throw new Error(message);
    }
  }, [loadRequests]);

  const createRecurringLines = useCallback(async (packetId, payload = {}) => {
    if (!packetId) return null;
    assertPacketInCurrentScope(packetId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-packets/${encodeURIComponent(packetId)}/lines/recurring`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.message || data?.error || `Create failed (${resp.status})`);
      }
      const updated = normalizePacket(data);
      setRequests(prev => {
        const next = Array.isArray(prev) ? prev.slice() : [];
        const index = next.findIndex(entry => entry.id === updated.id);
        if (index >= 0) {
          next[index] = updated;
          return next;
        }
        return [updated, ...next];
      });
      setSelectedRequestId(updated.id);
      return updated;
    } catch (err) {
      console.error("[Payments] failed to create recurring lines", err);
      const message = err.message || "Failed to create recurring lines";
      setError(message);
      throw new Error(message);
    }
  }, [assertPacketInCurrentScope]);

  const updateEvidence = useCallback(async (documentLinkId, payload = {}) => {
    if (!documentLinkId) return null;
    assertDocumentLinkInCurrentScope(documentLinkId);
    try {
      const resp = await apiFetch(
        `/api/finance/payment-documents/${encodeURIComponent(documentLinkId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload || {}),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.message || data?.error || `Update failed (${resp.status})`);
      }
      await loadRequests();
      return normalizeEvidenceItem(data);
    } catch (err) {
      console.error("[Payments] failed to update evidence", err);
      const message = err.message || "Failed to update evidence";
      setError(message);
      throw new Error(message);
    }
  }, [assertDocumentLinkInCurrentScope, loadRequests]);

  const addCommunication = useCallback(async payload => {
    if (!payload?.packetId) return null;
    assertPacketInCurrentScope(payload.packetId);
    try {
      const resp = await apiFetch("/api/finance/payment-communications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packetId: payload.packetId,
          subject: payload.subject ?? "Payment correspondence",
          recipients: payload.recipients ?? [],
          direction: payload.direction ?? "outbound",
          channel: payload.channel ?? "email",
          body: payload.body ?? null,
          template: payload.template ?? "manual",
          attachments: payload.attachments ?? [],
        }),
      });
      if (!resp.ok) {
        const message = (await resp.json().catch(() => ({}))).message || resp.statusText;
        throw new Error(message || "Failed to log communication");
      }
      const created = normalizeCommunication(await resp.json());
      if (created) {
        setCommunications(prev => [created, ...prev]);
      }
      return created;
    } catch (err) {
      console.error("[Payments] failed to log communication", err);
      return null;
    }
  }, [assertPacketInCurrentScope, setCommunications]);

  const sendPacketEmail = useCallback(async (packetId, note = null) => {
    if (!packetId) return null;
    return updatePacketStatus(packetId, "submitted", { notes: note });
  }, [updatePacketStatus]);

  const selectedRequest = useMemo(
    () => requests.find(entry => entry.id === activeSelectedRequestId) ?? null,
    [activeSelectedRequestId, requests],
  );

  const paymentTypeMappingLookup = useMemo(
    () => buildPaymentTypeMappingLookup(paymentTypeMapping),
    [paymentTypeMapping],
  );
  const paymentTypeOptions = useMemo(() => {
    const list = Array.isArray(paymentTypeMapping?.paymentTypes) ? paymentTypeMapping.paymentTypes : [];
    return list
      .map(entry => {
        const code = normalizePaymentTypeCode(entry?.code);
        if (!code) return null;
        return {
          value: code,
          label: entry?.label || code,
        };
      })
      .filter(Boolean);
  }, [paymentTypeMapping]);
  const paymentTypeOptionMap = useMemo(() => {
    const map = new Map();
    paymentTypeOptions.forEach(option => {
      if (!option?.value) return;
      map.set(option.value, option);
    });
    return map;
  }, [paymentTypeOptions]);
  const payeeTypeOptions = useMemo(() => {
    const list = Array.isArray(paymentTypeMapping?.payeeTypes) ? paymentTypeMapping.payeeTypes : [];
    return list
      .map(entry => {
        const code = normalizePayeeTypeCode(entry?.code);
        if (!code) return null;
        return {
          value: code,
          label: entry?.label || code,
          description: entry?.description || undefined,
        };
      })
      .filter(Boolean);
  }, [paymentTypeMapping]);
  const paymentTypeRecurrencePolicyLookup = useMemo(
    () => buildRecurrencePolicyLookup(paymentTypeMapping),
    [paymentTypeMapping],
  );

  const slaSnapshot = useMemo(() => computeSlaSnapshot(requests), [requests]);

  const value = useMemo(
    () => ({
      paymentSubmissionMode,
      requests,
      selectedRequest,
      selectedRequestId: activeSelectedRequestId,
      selectRequest,
      updatePacketStatus,
      validatePacket,
      updateLineStatus,
      updateLine,
      logPaymentFollowUp,
      deleteLine,
      createPacket,
      deletePacket,
      addPacketLines,
      createBatch,
      updateBatchStatus,
      createRecurringLines,
      updateEvidence,
      communications,
      addCommunication,
      sendPacketEmail,
      slaSnapshot,
      paymentTypeMapping,
      paymentTypeMappingLookup,
      paymentTypeOptions,
      paymentTypeOptionMap,
      payeeTypeOptions,
      paymentTypeRecurrencePolicyLookup,
      paymentTypeMappingLoading,
      loading,
      error,
      reloadRequests: loadRequests,
      reloadCommunications: loadCommunications,
      reloadPaymentTypeMapping: loadPaymentTypeMapping,
    }),
    [
      paymentSubmissionMode,
      requests,
      selectedRequest,
      activeSelectedRequestId,
      selectRequest,
      updatePacketStatus,
      validatePacket,
      updateLineStatus,
      updateLine,
      logPaymentFollowUp,
      deleteLine,
      createPacket,
      deletePacket,
      addPacketLines,
      createBatch,
      updateBatchStatus,
      createRecurringLines,
      updateEvidence,
      communications,
      addCommunication,
      sendPacketEmail,
      slaSnapshot,
      paymentTypeMapping,
      paymentTypeMappingLookup,
      paymentTypeOptions,
      paymentTypeOptionMap,
      payeeTypeOptions,
      paymentTypeRecurrencePolicyLookup,
      paymentTypeMappingLoading,
      loading,
      error,
      loadRequests,
      loadCommunications,
      loadPaymentTypeMapping,
    ],
  );

  return <PaymentsDataContext.Provider value={value}>{children}</PaymentsDataContext.Provider>;
};

export const usePaymentsData = () => {
  const context = useContext(PaymentsDataContext);
  if (!context) {
    throw new Error("usePaymentsData must be used within a PaymentsDataProvider");
  }
  return context;
};
